import React, { useState, useRef, useCallback, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import Editor from '@monaco-editor/react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { motion, AnimatePresence } from 'framer-motion'
import toast from 'react-hot-toast'
import { apiService } from '../../services/api'
import {
  Save,
  Eye,
  EyeOff,
  Smartphone,
  Tablet,
  Monitor,
  Globe,
  ArrowLeft,
  Code2,
  RefreshCw,
  ExternalLink,
  Loader2,
  FileCode,
  Palette,
  Search,
  Clock,
  ChevronLeft,
  Plus,
  Trash2,
  X,
  Home,
  CheckCircle,
  Image,
  Copy,
  Braces,
} from 'lucide-react'
import LoadingSpinner from '../common/LoadingSpinner'
import { useTheme } from '../ui/theme-provider'
import { INDUSTRIES } from '../../utils/industrySchemas'

const DEVICE_SIZES = {
  desktop: { width: '100%',   label: 'Desktop',  icon: Monitor },
  tablet:  { width: '768px',  label: 'Tablet',   icon: Tablet },
  mobile:  { width: '375px',  label: 'Mobile',   icon: Smartphone },
}

const CODE_TABS = [
  { id: 'html', label: 'HTML', language: 'html',       icon: FileCode },
  { id: 'css',  label: 'CSS',  language: 'css',        icon: Palette },
  { id: 'js',   label: 'JS',   language: 'javascript', icon: Code2 },
]

const CodeEditor = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const queryClient = useQueryClient()
  const { theme } = useTheme()

  const siteId   = searchParams.get('site')
  const clientId = searchParams.get('client')

  const [activeTab,      setActiveTab]      = useState('html')
  const [device,         setDevice]         = useState('desktop')
  const [showPreview,    setShowPreview]    = useState(true)
  const [liveRefresh,    setLiveRefresh]    = useState(false)
  const [hasUnsaved,     setHasUnsaved]     = useState(false)
  const [isPublishing,   setIsPublishing]   = useState(false)
  const [previewKey,     setPreviewKey]     = useState(0)
  const [publicUrl,      setPublicUrl]      = useState(null)

  // Pages state
  const [pages,          setPages]          = useState([])
  const [activePage,     setActivePage]     = useState(null)
  const [showAddPage,    setShowAddPage]    = useState(false)
  const [newPageName,    setNewPageName]    = useState('')
  const [newPageSlug,    setNewPageSlug]    = useState('')
  const [deletingPageId, setDeletingPageId] = useState(null)

  // Media picker state
  const [showMediaPicker, setShowMediaPicker] = useState(false)

  // Brand palette state
  const [showBrandPalette, setShowBrandPalette] = useState(false)

  // Variables panel state
  const [showVarsPanel, setShowVarsPanel] = useState(false)
  const [varsTab,       setVarsTab]       = useState(null)

  const editorRefs = useRef({})

  // Site search state
  const [searchQuery,    setSearchQuery]    = useState('')
  const [filteredSites,  setFilteredSites]  = useState([])

  const [code, setCode] = useState({ html: '', css: '', js: '' })
  const codeRef = useRef({ html: '', css: '', js: '' })
  const iframeRef = useRef(null)

  // ── Site search query (only when no ?site=) ──────────────────────────────
  const { data: allSites = [] } = useQuery(
    'all-sites',
    () => apiService.get('/sites').then(r => r.data.sites || []),
    { enabled: !siteId }
  )

  useEffect(() => {
    if (!siteId && allSites.length > 0 && filteredSites.length === 0 && searchQuery === '') {
      setFilteredSites(
        [...allSites]
          .filter(s => s.updated_at)
          .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))
          .slice(0, 3)
      )
    }
  }, [allSites, siteId])

  const handleSearchChange = (e) => {
    const q = e.target.value
    setSearchQuery(q)
    if (q.trim() === '') {
      setFilteredSites(
        [...allSites]
          .filter(s => s.updated_at)
          .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))
          .slice(0, 3)
      )
    } else {
      setFilteredSites(
        allSites.filter(s =>
          s.name.toLowerCase().includes(q.toLowerCase()) ||
          (s.company_name || '').toLowerCase().includes(q.toLowerCase())
        )
      )
    }
  }

  const handleSiteSelect = (s) => {
    navigate(`/admin/editor?site=${s.id}&client=${s.client_id}`)
  }

  // ── Load site ─────────────────────────────────────────────────────────────
  const { data: site, isLoading: siteLoading } = useQuery(
    ['site', siteId],
    () => apiService.get(`/sites/${siteId}`).then(r => r.data),
    { enabled: !!siteId }
  )

  // ── Reset editor state when the site changes ─────────────────────────────
  useEffect(() => {
    setActivePage(null)
    setPages([])
    setCode({ html: '', css: '', js: '' })
    codeRef.current = { html: '', css: '', js: '' }
    setHasUnsaved(false)
    setPublicUrl(null)
  }, [siteId])

  // ── Load client media ─────────────────────────────────────────────────────
  const { data: mediaItems = [] } = useQuery(
    ['media', clientId],
    () => apiService.get('/media', { params: { clientId } }).then(r => r.data.media || []),
    { enabled: !!clientId && showMediaPicker }
  )

  // ── Load pages ────────────────────────────────────────────────────────────
  const { data: pagesData, isLoading: pagesLoading, refetch: refetchPages } = useQuery(
    ['pages', siteId],
    () => apiService.get(`/sites/${siteId}/pages`).then(r => r.data.pages || []),
    { enabled: !!siteId }
  )

  // Load home page whenever pages data arrives (covers cache hits too)
  useEffect(() => {
    if (!pagesData || pagesData.length === 0) return
    setPages(pagesData)
    // Only auto-load if no page is active yet (don't override mid-session)
    setActivePage(prev => {
      if (prev) return prev
      const home = pagesData.find(p => p.is_home) || pagesData[0]
      // Only load HTML — CSS/JS come from the site-level effect below
      setCode(prev2 => ({ ...prev2, html: home.html_content || '' }))
      codeRef.current = { ...codeRef.current, html: home.html_content || '' }
      setPreviewKey(k => k + 1)
      return home
    })
  }, [pagesData])

  // Load site-level CSS/JS whenever the site changes (global styles for all pages)
  useEffect(() => {
    if (!site) return
    const siteCss = site.draft_css_content || site.css_content || ''
    const siteJs  = site.draft_js_content  || site.js_content  || ''
    setCode(prev => ({ ...prev, css: siteCss, js: siteJs }))
    codeRef.current = { ...codeRef.current, css: siteCss, js: siteJs }
  }, [site?.id])

  // Load a page — only updates HTML; CSS/JS remain site-level
  const loadPage = (page) => {
    setActivePage(page)
    setCode(prev => ({ ...prev, html: page.html_content || '' }))
    codeRef.current = { ...codeRef.current, html: page.html_content || '' }
    setHasUnsaved(false)
    setPreviewKey(k => k + 1)
  }

  // ── Page-level save mutation (HTML only) ─────────────────────────────────
  const savePageMutation = useMutation(
    ({ pageId, payload }) => apiService.put(`/sites/${siteId}/pages/${pageId}`, payload),
    {
      onSuccess: () => {
        toast.success('Saved')
        setHasUnsaved(false)
        refetchPages()
      },
      onError: () => toast.error('Failed to save page'),
    }
  )

  // ── Site-level save mutation (CSS + JS global) ────────────────────────────
  const saveSiteMutation = useMutation(
    (payload) => apiService.put(`/sites/${siteId}`, payload),
    {
      onError: () => toast.error('Failed to save global CSS/JS'),
    }
  )

  // ── Add page mutation ─────────────────────────────────────────────────────
  const addPageMutation = useMutation(
    (payload) => apiService.post(`/sites/${siteId}/pages`, payload),
    {
      onSuccess: (res) => {
        toast.success(`Page "${res.data.page.name}" created`)
        setShowAddPage(false)
        setNewPageName('')
        setNewPageSlug('')
        refetchPages().then(({ data }) => {
          const created = data?.pages?.find(p => p.id === res.data.page.id)
          if (created) loadPage(created)
        })
      },
      onError: (err) => {
        toast.error(err.response?.data?.error || 'Failed to create page')
      },
    }
  )

  // ── Delete page mutation ──────────────────────────────────────────────────
  const deletePageMutation = useMutation(
    (pageId) => apiService.delete(`/sites/${siteId}/pages/${pageId}`),
    {
      onSuccess: (_, deletedId) => {
        toast.success('Page deleted')
        setDeletingPageId(null)
        refetchPages().then(({ data }) => {
          const remaining = data?.pages || []
          if (activePage?.id === deletedId && remaining.length > 0) {
            loadPage(remaining.find(p => p.is_home) || remaining[0])
          }
        })
      },
      onError: (err) => {
        toast.error(err.response?.data?.error || 'Failed to delete page')
        setDeletingPageId(null)
      },
    }
  )

  // ── Publish mutation ──────────────────────────────────────────────────────
  const publishMutation = useMutation(
    () => apiService.post(`/sites/${siteId}/publish`),
    {
      onSuccess: (res) => {
        toast.success('Site published successfully!')
        setIsPublishing(false)
        if (res.data?.public_url) setPublicUrl(res.data.public_url)
        queryClient.invalidateQueries(['site', siteId])
      },
      onError: () => {
        toast.error('Failed to publish site')
        setIsPublishing(false)
      },
    }
  )

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleCodeChange = useCallback((value, lang) => {
    codeRef.current = { ...codeRef.current, [lang]: value || '' }
    setCode(prev => ({ ...prev, [lang]: value || '' }))
    setHasUnsaved(true)
    if (liveRefresh) setPreviewKey(k => k + 1)
  }, [liveRefresh])

  const refreshPreview = useCallback(() => setPreviewKey(k => k + 1), [])

  const handleSave = useCallback(() => {
    // Save CSS/JS globally to the site (applies to all pages)
    saveSiteMutation.mutate({
      css_content: codeRef.current.css,
      js_content:  codeRef.current.js,
    })
    // Save HTML to the current page
    if (activePage) {
      savePageMutation.mutate({
        pageId: activePage.id,
        payload: { html_content: codeRef.current.html },
      })
    }
  }, [activePage])

  const handlePageSwitch = useCallback((page) => {
    if (page.id === activePage?.id) return
    if (hasUnsaved && activePage) {
      // Auto-save HTML to the page being left
      savePageMutation.mutate({
        pageId: activePage.id,
        payload: { html_content: codeRef.current.html },
      })
      // Auto-save CSS/JS to the site (global)
      saveSiteMutation.mutate({
        css_content: codeRef.current.css,
        js_content:  codeRef.current.js,
      })
    }
    loadPage(page)
  }, [activePage, hasUnsaved])

  const handlePublish = useCallback(async () => {
    setIsPublishing(true)
    // Always save CSS/JS to site before publishing so they get promoted to published state
    const savePromises = [
      saveSiteMutation.mutateAsync({
        css_content: codeRef.current.css,
        js_content:  codeRef.current.js,
      }),
    ]
    if (activePage) {
      savePromises.push(savePageMutation.mutateAsync({
        pageId: activePage.id,
        payload: { html_content: codeRef.current.html },
      }))
    }
    await Promise.all(savePromises).catch(() => {})
    publishMutation.mutate()
  }, [hasUnsaved, activePage])

  const handleAddPage = () => {
    if (!newPageName.trim()) return
    const slug = newPageSlug.trim() || `/${newPageName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/, '')}`
    addPageMutation.mutate({ name: newPageName.trim(), slug })
  }

  // ── Insert raw text at cursor (used by Variables panel) ──────────────────
  const insertTextAtCursor = useCallback((text) => {
    const editor = editorRefs.current[activeTab]
    if (editor) {
      const selection = editor.getSelection()
      editor.executeEdits('insert-var', [{ range: selection, text, forceMoveMarkers: true }])
      editor.focus()
    } else {
      navigator.clipboard.writeText(text)
      toast.success('Copied to clipboard')
    }
  }, [activeTab])

  // ── Insert media URL at cursor ────────────────────────────────────────────
  const insertMediaAtCursor = useCallback((url) => {
    const editor = editorRefs.current[activeTab]
    if (!editor) {
      navigator.clipboard.writeText(url)
      toast.success('URL copied to clipboard')
      setShowMediaPicker(false)
      return
    }
    const selection = editor.getSelection()
    const text = activeTab === 'html'
      ? `<img src="${url}" alt="" />`
      : activeTab === 'css'
      ? `url('${url}')`
      : `"${url}"`
    editor.executeEdits('media-picker', [{ range: selection, text, forceMoveMarkers: true }])
    editor.focus()
    setShowMediaPicker(false)
  }, [activeTab])

  // Auto-generate slug from name
  const handlePageNameChange = (e) => {
    const name = e.target.value
    setNewPageName(name)
    setNewPageSlug(`/${name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/, '')}`)
  }

  // ── PostMessage: iframe link interception ─────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (e.data?.type !== 'navigate') return
      const slug = e.data.slug
      const target = pages.find(p => p.slug === slug || p.slug === `/${slug.replace(/^\//, '')}`)
      if (target) {
        handlePageSwitch(target)
      } else {
        toast(`Page "${slug}" not found in this site`, { icon: '🔗' })
      }
    }
    window.addEventListener('message', handler)
    return () => window.removeEventListener('message', handler)
  }, [pages, handlePageSwitch])

  // ── Keyboard shortcut: Cmd/Ctrl+S ─────────────────────────────────────────
  useEffect(() => {
    const onKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault()
        handleSave()
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [handleSave])

  // ── Build preview document ─────────────────────────────────────────────────
  // Mirror exactly how customDomainRouter injects CSS/JS into the live site:
  // inject into the existing HTML structure rather than wrapping it, so that
  // code.js doesn't end up after </html> and get silently ignored by the browser.
  const buildPreviewDoc = () => {
    let doc = code.html || '<!DOCTYPE html><html><head></head><body></body></html>'

    // Prevent scroll chaining: overscroll-behavior:none stops the iframe from
    // propagating scrollIntoView / wheel events up to the parent editor page.
    const cspAndCss = `  <meta http-equiv="Content-Security-Policy" content="default-src * data: blob: 'unsafe-inline' 'unsafe-eval';">\n  <style>html,body{overscroll-behavior:none;}${code.css}</style>`
    doc = doc.includes('</head>')
      ? doc.replace('</head>', `${cspAndCss}\n</head>`)
      : `<head>${cspAndCss}</head>\n${doc}`

    // Use window.scrollTo() instead of scrollIntoView() to avoid cross-frame scroll
    // chaining: scrollIntoView() bubbles up through nested scroll containers including
    // the parent browsing context, causing the editor page to shift. window.scrollTo()
    // is scoped to the iframe's own window and does not propagate upward.
    const clickInterceptor = `(function(){document.addEventListener('click',function(e){var a=e.target.closest('a[href]');if(!a)return;var href=a.getAttribute('href');if(!href||href.startsWith('mailto:')||href.startsWith('tel:'))return;if(href.startsWith('#')){e.preventDefault();e.stopPropagation();var target=document.querySelector(href);if(target){var top=target.getBoundingClientRect().top+window.pageYOffset;window.scrollTo({top:top,behavior:'smooth'});}return;}if(!href.startsWith('http://')&&!href.startsWith('https://')&&!href.startsWith('//')){e.preventDefault();e.stopPropagation();window.parent.postMessage({type:'navigate',slug:href},'*');}},true);})();`
    const jsBlock = `<script>${clickInterceptor}\n${code.js}<\/script>`
    doc = doc.includes('</body>')
      ? doc.replace('</body>', `${jsBlock}\n</body>`)
      : `${doc}\n${jsBlock}`

    return doc
  }
  const previewDoc = buildPreviewDoc()

  const monacoTheme = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches)
    ? 'vs-dark'
    : 'light'

  // ── Site selection screen ──────────────────────────────────────────────────
  if (!siteId) {
    return (
      <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: 'var(--bg-0)', overflow: 'hidden' }}>
        {/* Header */}
        <div style={{ background: 'var(--surface)', borderBottom: '1px solid var(--border)', padding: '12px 24px', flexShrink: 0 }}>
          <div className="flex items-center gap-3">
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => navigate('/admin/sites')}
            >
              <ChevronLeft style={{ width: 16, height: 16, marginRight: 4 }} />
              Back to Sites
            </button>
            <div style={{ width: 1, height: 20, background: 'var(--border)' }} />
            <div className="flex items-center gap-2">
              <Code2 style={{ width: 20, height: 20, color: 'var(--accent)' }} />
              <h1 style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text)', margin: 0 }}>Code Editor</h1>
            </div>
          </div>
        </div>

        {/* Body */}
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 32, overflow: 'auto' }}>
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
            style={{ width: '100%', maxWidth: 640 }}
          >
            <div className="card" style={{ padding: 32 }}>
              {/* Card header */}
              <div style={{ textAlign: 'center', marginBottom: 24 }}>
                <div style={{
                  margin: '0 auto 16px',
                  padding: 12,
                  background: 'var(--accent-soft)',
                  borderRadius: 'var(--radius-lg)',
                  width: 'fit-content',
                }}>
                  <Search style={{ width: 32, height: 32, color: 'var(--accent)' }} />
                </div>
                <h2 className="h2" style={{ marginBottom: 8 }}>Select a Website to Edit</h2>
                <p className="text-soft" style={{ fontSize: '0.9375rem', margin: 0 }}>
                  Search and select a site to open in the code editor
                </p>
              </div>

              {/* Search input */}
              <div style={{ position: 'relative', marginBottom: 16 }}>
                <Search style={{
                  position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
                  width: 16, height: 16, color: 'var(--text-muted)', pointerEvents: 'none',
                }} />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  placeholder="Search by site name or company..."
                  autoFocus
                  className="input w-full"
                  style={{ paddingLeft: 40 }}
                />
              </div>

              {/* Results */}
              <AnimatePresence>
                {filteredSites.length > 0 ? (
                  <motion.div
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6 }}
                    style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius)', overflow: 'hidden' }}
                  >
                    {searchQuery === '' && (
                      <div style={{
                        padding: '8px 16px',
                        background: 'var(--bg-1)',
                        borderBottom: '1px solid var(--border)',
                      }}>
                        <p style={{ fontSize: '0.75rem', fontWeight: 500, color: 'var(--text-muted)', margin: 0 }}>Recently edited</p>
                      </div>
                    )}
                    {filteredSites.map((s, i) => (
                      <motion.button
                        key={s.id}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: i * 0.04 }}
                        onClick={() => handleSiteSelect(s)}
                        style={{
                          width: '100%',
                          textAlign: 'left',
                          padding: '12px 16px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          background: 'transparent',
                          border: 'none',
                          borderBottom: i < filteredSites.length - 1 ? '1px solid var(--border)' : 'none',
                          cursor: 'pointer',
                          transition: 'background 0.15s',
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-hover)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                      >
                        <div style={{ minWidth: 0 }}>
                          <p style={{ fontWeight: 500, color: 'var(--text)', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.name}</p>
                          <p style={{ fontSize: '0.875rem', color: 'var(--text-soft)', margin: '2px 0 0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.company_name}</p>
                          {s.updated_at && (
                            <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', margin: '4px 0 0', display: 'flex', alignItems: 'center', gap: 4 }}>
                              <Clock style={{ width: 12, height: 12 }} />
                              Updated {new Date(s.updated_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                            </p>
                          )}
                        </div>
                        <span className={`badge ${s.status === 'published' ? 'badge-success' : 'badge-warning'}`} style={{ marginLeft: 16, flexShrink: 0 }}>
                          {s.status}
                        </span>
                      </motion.button>
                    ))}
                  </motion.div>
                ) : searchQuery.trim() !== '' ? (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-soft)' }}
                  >
                    <Search style={{ width: 32, height: 32, margin: '0 auto 8px', opacity: 0.3, display: 'block' }} />
                    <p style={{ fontSize: '0.875rem', margin: 0 }}>No sites found for "{searchQuery}"</p>
                  </motion.div>
                ) : null}
              </AnimatePresence>
            </div>
          </motion.div>
        </div>
      </div>
    )
  }

  if (siteLoading || pagesLoading) {
    return (
      <div className="flex items-center justify-center" style={{ height: '100vh' }}>
        <LoadingSpinner size="lg" text="Loading editor..." />
      </div>
    )
  }

  // Site exists but has no pages yet (e.g. created before auto-home-page was added)
  if (!pagesLoading && pages.length === 0) {
    return (
      <div className="flex items-center justify-center" style={{ height: '100vh', background: 'var(--bg-0)' }}>
        <div style={{ textAlign: 'center' }}>
          <p style={{ color: 'var(--text-soft)', marginBottom: 16 }}>This site has no pages yet.</p>
          <button
            className="btn btn-primary"
            onClick={() => addPageMutation.mutate({ name: 'Home', slug: '/', is_home: true })}
            disabled={addPageMutation.isLoading}
          >
            {addPageMutation.isLoading ? 'Creating...' : 'Create Home Page'}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: 'var(--bg-0)', overflow: 'hidden' }}>

      {/* ── Top Toolbar ── */}
      <div style={{ borderBottom: '1px solid var(--border)', background: 'var(--surface)', flexShrink: 0 }}>

        {/* Main row: back + name (left) | Save + Publish always visible (right) */}
        <div className="flex items-center justify-between" style={{ padding: '8px 16px' }}>

          {/* Left: back + site name */}
          <div className="flex items-center gap-3" style={{ minWidth: 0 }}>
            <button
              className="btn btn-ghost btn-icon"
              style={{ flexShrink: 0 }}
              onClick={() => navigate(clientId ? `/admin/clients/${clientId}` : '/admin/sites')}
              title="Back"
            >
              <ArrowLeft style={{ width: 16, height: 16 }} />
            </button>
            <div style={{ minWidth: 0 }}>
              <p style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--text)', lineHeight: 1, margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {site?.name || 'Untitled Site'}
              </p>
              <p style={{ fontSize: '0.75rem', color: 'var(--text-soft)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {site?.company_name || ''}
              </p>
            </div>
            {hasUnsaved && (
              <span style={{ flexShrink: 0, fontSize: '0.75rem', color: 'var(--warning)', fontWeight: 500 }}>• Unsaved</span>
            )}
          </div>

          {/* Right: utility controls (desktop only) + Save + Publish */}
          <div className="flex items-center gap-2" style={{ flexShrink: 0 }}>

            {/* Utility controls — hidden on mobile, shown on desktop */}
            <div className="hidden sm:flex items-center gap-2">
              {/* Device picker */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 2, border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 4 }}>
                {Object.entries(DEVICE_SIZES).map(([key, { label, icon: Icon }]) => (
                  <button
                    key={key}
                    onClick={() => setDevice(key)}
                    title={label}
                    style={{
                      padding: '6px',
                      borderRadius: 'var(--radius-sm)',
                      border: 'none',
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                      background: device === key ? 'var(--accent)' : 'transparent',
                      color: device === key ? '#fff' : 'var(--text-muted)',
                    }}
                  >
                    <Icon style={{ width: 14, height: 14 }} />
                  </button>
                ))}
              </div>

              {/* Live refresh toggle */}
              <button
                onClick={() => setLiveRefresh(v => !v)}
                title="Live refresh"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '6px 10px',
                  borderRadius: 'var(--radius)',
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  border: `1px solid ${liveRefresh ? 'var(--success)' : 'var(--border)'}`,
                  background: liveRefresh ? 'color-mix(in srgb, var(--success) 12%, transparent)' : 'transparent',
                  color: liveRefresh ? 'var(--success)' : 'var(--text-muted)',
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                }}
              >
                <RefreshCw style={{ width: 12, height: 12 }} className={liveRefresh ? 'animate-spin-slow' : ''} />
                Live
              </button>

              <button className="btn btn-ghost btn-icon" onClick={refreshPreview} title="Refresh preview">
                <RefreshCw style={{ width: 16, height: 16 }} />
              </button>

              <button className="btn btn-ghost btn-icon" onClick={() => setShowPreview(v => !v)} title="Toggle preview">
                {showPreview ? <EyeOff style={{ width: 16, height: 16 }} /> : <Eye style={{ width: 16, height: 16 }} />}
              </button>

              <button
                className="btn btn-ghost btn-icon"
                title="Open preview in new tab"
                onClick={() => {
                  const blob = new Blob([previewDoc], { type: 'text/html' })
                  window.open(URL.createObjectURL(blob), '_blank')
                }}
              >
                <ExternalLink style={{ width: 16, height: 16 }} />
              </button>

              {/* Live URL — desktop only */}
              {publicUrl && (
                <a
                  href={publicUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Open live site"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '6px 10px',
                    borderRadius: 'var(--radius)',
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    border: '1px solid var(--success)',
                    background: 'color-mix(in srgb, var(--success) 12%, transparent)',
                    color: 'var(--success)',
                    textDecoration: 'none',
                    maxWidth: 180,
                    transition: 'background 0.15s',
                  }}
                >
                  <CheckCircle style={{ width: 12, height: 12, flexShrink: 0 }} />
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {publicUrl.replace('https://', '')}
                  </span>
                </a>
              )}
            </div>

            {/* Save + Publish — always visible */}
            <button
              className="btn btn-sm"
              style={{ border: '1px solid var(--border)', background: 'transparent', color: 'var(--text)', display: 'flex', alignItems: 'center', gap: 6 }}
              onClick={handleSave}
              disabled={savePageMutation.isLoading}
            >
              {savePageMutation.isLoading
                ? <Loader2 style={{ width: 14, height: 14 }} className="animate-spin" />
                : <Save style={{ width: 14, height: 14 }} />}
              Save
            </button>

            <button
              className="btn btn-primary btn-sm"
              style={{ display: 'flex', alignItems: 'center', gap: 6 }}
              onClick={handlePublish}
              disabled={isPublishing || publishMutation.isLoading}
            >
              {isPublishing
                ? <Loader2 style={{ width: 14, height: 14 }} className="animate-spin" />
                : <Globe style={{ width: 14, height: 14 }} />}
              Publish
            </button>
          </div>
        </div>

        {/* Mobile utility row — shown only on small screens */}
        <div className="flex sm:hidden items-center gap-2" style={{ padding: '0 16px 8px', overflowX: 'auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 2, border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 4, flexShrink: 0 }}>
            {Object.entries(DEVICE_SIZES).map(([key, { label, icon: Icon }]) => (
              <button
                key={key}
                onClick={() => setDevice(key)}
                title={label}
                style={{
                  padding: 6,
                  borderRadius: 'var(--radius-sm)',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                  background: device === key ? 'var(--accent)' : 'transparent',
                  color: device === key ? '#fff' : 'var(--text-muted)',
                }}
              >
                <Icon style={{ width: 14, height: 14 }} />
              </button>
            ))}
          </div>

          <button
            onClick={() => setLiveRefresh(v => !v)}
            style={{
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '6px 10px',
              borderRadius: 'var(--radius)',
              fontSize: '0.75rem',
              fontWeight: 500,
              border: `1px solid ${liveRefresh ? 'var(--success)' : 'var(--border)'}`,
              background: liveRefresh ? 'color-mix(in srgb, var(--success) 12%, transparent)' : 'transparent',
              color: liveRefresh ? 'var(--success)' : 'var(--text-muted)',
              cursor: 'pointer',
              transition: 'all 0.15s',
            }}
          >
            <RefreshCw style={{ width: 12, height: 12 }} className={liveRefresh ? 'animate-spin-slow' : ''} />
            Live
          </button>

          <button className="btn btn-ghost btn-icon" style={{ flexShrink: 0 }} onClick={refreshPreview} title="Refresh preview">
            <RefreshCw style={{ width: 16, height: 16 }} />
          </button>

          <button className="btn btn-ghost btn-icon" style={{ flexShrink: 0 }} onClick={() => setShowPreview(v => !v)} title="Toggle preview">
            {showPreview ? <EyeOff style={{ width: 16, height: 16 }} /> : <Eye style={{ width: 16, height: 16 }} />}
          </button>

          <button
            className="btn btn-ghost btn-icon"
            style={{ flexShrink: 0 }}
            title="Open preview in new tab"
            onClick={() => {
              const blob = new Blob([previewDoc], { type: 'text/html' })
              window.open(URL.createObjectURL(blob), '_blank')
            }}
          >
            <ExternalLink style={{ width: 16, height: 16 }} />
          </button>

          {publicUrl && (
            <a
              href={publicUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '6px 10px',
                borderRadius: 'var(--radius)',
                fontSize: '0.75rem',
                fontWeight: 500,
                border: '1px solid var(--success)',
                background: 'color-mix(in srgb, var(--success) 12%, transparent)',
                color: 'var(--success)',
                textDecoration: 'none',
                transition: 'background 0.15s',
              }}
            >
              <CheckCircle style={{ width: 12, height: 12, flexShrink: 0 }} />
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 120 }}>
                {publicUrl.replace('https://', '')}
              </span>
            </a>
          )}
        </div>
      </div>

      {/* ── Page tab strip ── */}
      <div
        className="flex items-center"
        style={{
          borderBottom: '1px solid var(--border)',
          background: 'var(--surface)',
          padding: '0 8px',
          flexShrink: 0,
          overflowX: 'auto',
        }}
      >
        {pages.map((page) => (
          <button
            key={page.id}
            onClick={() => handlePageSwitch(page)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '8px 12px',
              fontSize: '0.75rem',
              fontWeight: 500,
              borderBottom: `2px solid ${activePage?.id === page.id ? 'var(--accent)' : 'transparent'}`,
              color: activePage?.id === page.id ? 'var(--accent)' : 'var(--text-muted)',
              background: 'transparent',
              border: 'none',
              borderBottom: `2px solid ${activePage?.id === page.id ? 'var(--accent)' : 'transparent'}`,
              marginBottom: -1,
              cursor: 'pointer',
              flexShrink: 0,
              whiteSpace: 'nowrap',
              transition: 'color 0.15s',
            }}
            className="group"
            onMouseEnter={e => { if (activePage?.id !== page.id) e.currentTarget.style.color = 'var(--text-soft)' }}
            onMouseLeave={e => { if (activePage?.id !== page.id) e.currentTarget.style.color = 'var(--text-muted)' }}
          >
            {page.is_home ? <Home style={{ width: 12, height: 12 }} /> : null}
            {page.name}
            {!page.is_home && (
              <span
                role="button"
                title="Delete page"
                onClick={(e) => {
                  e.stopPropagation()
                  setDeletingPageId(page.id)
                }}
                style={{ marginLeft: 4, color: 'var(--text-faint)', display: 'flex', borderRadius: 'var(--radius-sm)', transition: 'color 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.color = 'var(--danger)'}
                onMouseLeave={e => e.currentTarget.style.color = 'var(--text-faint)'}
              >
                <X style={{ width: 12, height: 12 }} />
              </span>
            )}
          </button>
        ))}

        {/* Add page button */}
        <button
          onClick={() => setShowAddPage(true)}
          title="Add page"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            padding: '8px 12px',
            fontSize: '0.75rem',
            color: 'var(--text-muted)',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            flexShrink: 0,
            transition: 'color 0.15s',
          }}
          onMouseEnter={e => e.currentTarget.style.color = 'var(--accent)'}
          onMouseLeave={e => e.currentTarget.style.color = 'var(--text-muted)'}
        >
          <Plus style={{ width: 14, height: 14 }} />
          Add page
        </button>
      </div>

      {/* ── Main area ── */}
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

        {/* ── Editor pane ── */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          borderRight: '1px solid var(--border)',
          transition: 'width 0.2s',
          width: showPreview ? '50%' : '100%',
        }}>
          <>
            {/* Code tab bar */}
            <div
              className="flex items-center justify-between"
              style={{ borderBottom: '1px solid var(--border)', background: 'var(--surface)', padding: '0 8px' }}
            >
              <div style={{ display: 'flex', alignItems: 'center' }}>
                {CODE_TABS.map(({ id, label, icon: Icon }) => (
                  <button
                    key={id}
                    onClick={() => setActiveTab(id)}
                    title={id !== 'html' ? 'Global — applies to all pages' : undefined}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      padding: '10px 12px',
                      fontSize: '0.75rem',
                      fontWeight: 500,
                      borderBottom: `2px solid ${activeTab === id ? 'var(--accent)' : 'transparent'}`,
                      color: activeTab === id ? 'var(--accent)' : 'var(--text-muted)',
                      background: 'transparent',
                      border: 'none',
                      borderBottom: `2px solid ${activeTab === id ? 'var(--accent)' : 'transparent'}`,
                      marginBottom: -1,
                      cursor: 'pointer',
                      transition: 'color 0.15s',
                    }}
                    onMouseEnter={e => { if (activeTab !== id) e.currentTarget.style.color = 'var(--text-soft)' }}
                    onMouseLeave={e => { if (activeTab !== id) e.currentTarget.style.color = 'var(--text-muted)' }}
                  >
                    <Icon style={{ width: 14, height: 14 }} />
                    <span className="hidden sm:inline">{label}</span>
                    {id !== 'html' && (
                      <span
                        className="hidden sm:inline"
                        style={{
                          fontSize: '0.625rem',
                          fontWeight: 500,
                          padding: '2px 4px',
                          borderRadius: 'var(--radius-sm)',
                          background: 'var(--accent-soft)',
                          color: 'var(--accent)',
                          lineHeight: 1,
                        }}
                      >
                        Global
                      </span>
                    )}
                  </button>
                ))}
              </div>

              {/* Toolbar actions */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                {site?.brand_colors?.length > 0 && (
                  <button
                    onClick={() => setShowBrandPalette(v => !v)}
                    title="Brand palette"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      padding: '6px 10px',
                      fontSize: '0.75rem',
                      fontWeight: 500,
                      borderRadius: 'var(--radius)',
                      border: 'none',
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                      background: showBrandPalette ? 'var(--accent-soft)' : 'transparent',
                      color: showBrandPalette ? 'var(--accent)' : 'var(--text-muted)',
                    }}
                    onMouseEnter={e => { if (!showBrandPalette) { e.currentTarget.style.background = 'var(--accent-soft)'; e.currentTarget.style.color = 'var(--accent)' } }}
                    onMouseLeave={e => { if (!showBrandPalette) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-muted)' } }}
                  >
                    <Palette style={{ width: 14, height: 14 }} />
                    <span className="hidden sm:inline">Brand</span>
                  </button>
                )}
                {site?.industry && INDUSTRIES[site.industry] && (
                  <button
                    onClick={() => {
                      setShowVarsPanel(v => !v)
                      if (!varsTab) setVarsTab(INDUSTRIES[site.industry].tabs[0]?.id)
                    }}
                    title="Content variables"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      padding: '6px 10px',
                      fontSize: '0.75rem',
                      fontWeight: 500,
                      borderRadius: 'var(--radius)',
                      border: 'none',
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                      background: showVarsPanel ? 'var(--accent-soft)' : 'transparent',
                      color: showVarsPanel ? 'var(--accent)' : 'var(--text-muted)',
                    }}
                    onMouseEnter={e => { if (!showVarsPanel) { e.currentTarget.style.background = 'var(--accent-soft)'; e.currentTarget.style.color = 'var(--accent)' } }}
                    onMouseLeave={e => { if (!showVarsPanel) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-muted)' } }}
                  >
                    <Braces style={{ width: 14, height: 14 }} />
                    <span className="hidden sm:inline">Variables</span>
                  </button>
                )}
                <button
                  onClick={() => setShowMediaPicker(true)}
                  title="Insert media"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '6px 10px',
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    borderRadius: 'var(--radius)',
                    border: 'none',
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                    background: 'transparent',
                    color: 'var(--text-muted)',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'var(--accent-soft)'; e.currentTarget.style.color = 'var(--accent)' }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--text-muted)' }}
                >
                  <Image style={{ width: 14, height: 14 }} />
                  <span className="hidden sm:inline">Media</span>
                </button>
              </div>
            </div>

            {/* Variables panel */}
            {showVarsPanel && site?.industry && INDUSTRIES[site.industry] && (() => {
              const schema = INDUSTRIES[site.industry]
              const activeVarsTabSchema = schema.tabs.find(t => t.id === varsTab) || schema.tabs[0]
              return (
                <div style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg-1)' }}>
                  {/* Tab selector */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '8px 12px 0', overflowX: 'auto' }}>
                    <span style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-faint)', flexShrink: 0, marginRight: 4 }}>
                      {schema.icon} {schema.label}
                    </span>
                    {schema.tabs.map(tab => (
                      <button
                        key={tab.id}
                        onClick={() => setVarsTab(tab.id)}
                        style={{
                          flexShrink: 0,
                          padding: '4px 10px',
                          fontSize: '0.75rem',
                          fontWeight: 500,
                          borderRadius: 'var(--radius-sm) var(--radius-sm) 0 0',
                          borderBottom: `2px solid ${(varsTab || schema.tabs[0].id) === tab.id ? 'var(--accent)' : 'transparent'}`,
                          color: (varsTab || schema.tabs[0].id) === tab.id ? 'var(--accent)' : 'var(--text-muted)',
                          background: (varsTab || schema.tabs[0].id) === tab.id ? 'var(--surface)' : 'transparent',
                          border: 'none',
                          borderBottom: `2px solid ${(varsTab || schema.tabs[0].id) === tab.id ? 'var(--accent)' : 'transparent'}`,
                          cursor: 'pointer',
                          whiteSpace: 'nowrap',
                          transition: 'color 0.15s',
                        }}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>

                  {/* Fields for active tab */}
                  <div style={{ padding: '10px 12px', maxHeight: 192, overflowY: 'auto', background: 'var(--surface)' }}>
                    {activeVarsTabSchema?.fields.map(field => {
                      if (field.type === 'list') {
                        const subKeys = field.subFields.map(sf => `  {{${sf.key}}}`).join('\n')
                        const loopSnippet = `{{#${field.key}}}\n${subKeys}\n{{/${field.key}}}`
                        return (
                          <div key={field.key} style={{ marginBottom: 8 }}>
                            <p style={{ fontSize: '0.75rem', fontWeight: 500, color: 'var(--text-soft)', margin: '0 0 4px' }}>
                              {field.label} <span style={{ color: 'var(--text-muted)' }}>(repeating list)</span>
                            </p>
                            <div style={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: 8,
                              background: 'var(--bg-1)',
                              borderRadius: 'var(--radius)',
                              padding: 8,
                              border: '1px solid var(--border)',
                            }}>
                              <pre style={{ fontSize: '0.75rem', fontFamily: 'var(--font-mono)', color: 'var(--accent)', flex: 1, whiteSpace: 'pre', margin: 0 }}>{loopSnippet}</pre>
                              <button
                                onClick={() => insertTextAtCursor(loopSnippet)}
                                title="Insert loop"
                                style={{ flexShrink: 0, padding: 4, borderRadius: 'var(--radius-sm)', border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--text-muted)', transition: 'color 0.15s' }}
                                onMouseEnter={e => e.currentTarget.style.color = 'var(--accent)'}
                                onMouseLeave={e => e.currentTarget.style.color = 'var(--text-muted)'}
                              >
                                <Copy style={{ width: 12, height: 12 }} />
                              </button>
                            </div>
                          </div>
                        )
                      }
                      if (field.type === 'multi-image') {
                        const snippet = `{{#${field.key}}}\n  <img src="{{.}}" alt="" />\n{{/${field.key}}}`
                        return (
                          <div key={field.key} style={{ marginBottom: 8 }}>
                            <p style={{ fontSize: '0.75rem', fontWeight: 500, color: 'var(--text-soft)', margin: '0 0 4px' }}>
                              {field.label} <span style={{ color: 'var(--text-muted)' }}>(image gallery)</span>
                            </p>
                            <div style={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: 8,
                              background: 'var(--bg-1)',
                              borderRadius: 'var(--radius)',
                              padding: 8,
                              border: '1px solid var(--border)',
                            }}>
                              <pre style={{ fontSize: '0.75rem', fontFamily: 'var(--font-mono)', color: 'var(--accent)', flex: 1, whiteSpace: 'pre', margin: 0 }}>{snippet}</pre>
                              <button
                                onClick={() => insertTextAtCursor(snippet)}
                                title="Insert"
                                style={{ flexShrink: 0, padding: 4, borderRadius: 'var(--radius-sm)', border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--text-muted)', transition: 'color 0.15s' }}
                                onMouseEnter={e => e.currentTarget.style.color = 'var(--accent)'}
                                onMouseLeave={e => e.currentTarget.style.color = 'var(--text-muted)'}
                              >
                                <Copy style={{ width: 12, height: 12 }} />
                              </button>
                            </div>
                          </div>
                        )
                      }
                      if (field.type === 'hours') {
                        const snippet = `{{#hours}}\n  <p>{{day}}: {{openTime}}–{{closeTime}}</p>\n{{/hours}}`
                        return (
                          <div key={field.key} style={{ marginBottom: 8 }}>
                            <p style={{ fontSize: '0.75rem', fontWeight: 500, color: 'var(--text-soft)', margin: '0 0 4px' }}>
                              Opening Hours <span style={{ color: 'var(--text-muted)' }}>(days loop)</span>
                            </p>
                            <div style={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: 8,
                              background: 'var(--bg-1)',
                              borderRadius: 'var(--radius)',
                              padding: 8,
                              border: '1px solid var(--border)',
                            }}>
                              <pre style={{ fontSize: '0.75rem', fontFamily: 'var(--font-mono)', color: 'var(--accent)', flex: 1, whiteSpace: 'pre', margin: 0 }}>{snippet}</pre>
                              <button
                                onClick={() => insertTextAtCursor(snippet)}
                                title="Insert"
                                style={{ flexShrink: 0, padding: 4, borderRadius: 'var(--radius-sm)', border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--text-muted)', transition: 'color 0.15s' }}
                                onMouseEnter={e => e.currentTarget.style.color = 'var(--accent)'}
                                onMouseLeave={e => e.currentTarget.style.color = 'var(--text-muted)'}
                              >
                                <Copy style={{ width: 12, height: 12 }} />
                              </button>
                            </div>
                          </div>
                        )
                      }
                      // Simple fields: text, textarea, image, url
                      const token = `{{${field.key}}}`
                      return (
                        <button
                          key={field.key}
                          onClick={() => insertTextAtCursor(token)}
                          title={`Insert ${token}`}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 4,
                            padding: '4px 8px',
                            marginRight: 6,
                            marginBottom: 4,
                            borderRadius: 'var(--radius-sm)',
                            background: 'var(--bg-1)',
                            border: '1px solid var(--border)',
                            cursor: 'pointer',
                            transition: 'all 0.15s',
                          }}
                          onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.background = 'var(--accent-soft)' }}
                          onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.background = 'var(--bg-1)' }}
                        >
                          <span style={{ fontSize: '0.75rem', fontFamily: 'var(--font-mono)', color: 'var(--accent)' }}>{token}</span>
                          <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{field.label}</span>
                        </button>
                      )
                    })}
                  </div>
                </div>
              )
            })()}

            {/* Brand palette strip */}
            {showBrandPalette && site?.brand_colors?.length > 0 && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '8px 12px',
                borderBottom: '1px solid var(--border)',
                background: 'var(--bg-1)',
                flexWrap: 'wrap',
              }}>
                <span style={{ fontSize: '0.75rem', fontWeight: 500, color: 'var(--text-soft)', flexShrink: 0 }}>
                  Brand Colors
                </span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                  {site.brand_colors.map((color) => (
                    <button
                      key={color}
                      title={`Copy ${color}`}
                      onClick={() => {
                        navigator.clipboard.writeText(color)
                        toast.success(`${color} copied`)
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        padding: '4px 8px',
                        borderRadius: 'var(--radius-sm)',
                        border: '1px solid var(--border)',
                        background: 'var(--surface)',
                        cursor: 'pointer',
                        transition: 'border-color 0.15s',
                      }}
                      onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--border-strong)'}
                      onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}
                    >
                      <span
                        style={{ width: 14, height: 14, borderRadius: 'var(--radius-sm)', flexShrink: 0, border: '1px solid rgba(0,0,0,0.1)', backgroundColor: color }}
                      />
                      <span style={{ fontSize: '0.75rem', fontFamily: 'var(--font-mono)', color: 'var(--text-soft)' }}>
                        {color}
                      </span>
                      <Copy style={{ width: 10, height: 10, color: 'var(--text-faint)' }} />
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Monaco editor */}
            <div style={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
              {CODE_TABS.map(({ id, language }) => (
                <div
                  key={id}
                  style={{
                    position: 'absolute',
                    inset: 0,
                    visibility: activeTab !== id ? 'hidden' : 'visible',
                    pointerEvents: activeTab !== id ? 'none' : 'auto',
                  }}
                >
                  <Editor
                    height="100%"
                    language={language}
                    value={code[id]}
                    theme={monacoTheme}
                    onChange={(val) => handleCodeChange(val, id)}
                    onMount={(editor) => { editorRefs.current[id] = editor }}
                    options={{
                      fontSize: 13,
                      fontFamily: '"JetBrains Mono", "Fira Code", monospace',
                      minimap: { enabled: false },
                      scrollBeyondLastLine: false,
                      wordWrap: 'on',
                      lineNumbers: 'on',
                      folding: true,
                      automaticLayout: true,
                      tabSize: 2,
                      padding: { top: 12, bottom: 12 },
                    }}
                  />
                </div>
              ))}
            </div>
          </>
        </div>

        {/* ── Preview pane ── */}
        {showPreview && (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: 'var(--bg-1)', overflow: 'hidden' }}>
            <div
              className="flex items-center justify-between"
              style={{ padding: '8px 16px', background: 'var(--surface)', borderBottom: '1px solid var(--border)' }}
            >
              <span style={{ fontSize: '0.75rem', color: 'var(--text-faint)', fontWeight: 500 }}>
                Preview — {activePage?.name || 'Home'} · {DEVICE_SIZES[device].label}
              </span>
              {activePage && (
                <span style={{ fontSize: '0.75rem', color: 'var(--text-faint)', fontFamily: 'var(--font-mono)' }}>
                  {activePage.slug}
                </span>
              )}
            </div>
            <div style={{ flex: 1, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowX: 'auto', overflowY: 'hidden', overscrollBehavior: 'contain', padding: 16 }}>
              <div
                style={{ width: DEVICE_SIZES[device].width, maxWidth: '100%', height: '100%', background: '#fff', boxShadow: 'var(--shadow-lg)', borderRadius: 'var(--radius)', overflow: 'hidden', transition: 'width 0.3s' }}
              >
                <iframe
                  key={previewKey}
                  ref={iframeRef}
                  srcDoc={previewDoc}
                  title="Site Preview"
                  style={{ width: '100%', height: '100%', border: 'none' }}
                  sandbox="allow-scripts allow-forms allow-popups"
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Add page modal ── */}
      <AnimatePresence>
        {showAddPage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="modal-backdrop"
            onClick={() => setShowAddPage(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 10 }}
              transition={{ duration: 0.15 }}
              onClick={(e) => e.stopPropagation()}
              className="modal"
              style={{ maxWidth: 400 }}
            >
              <div className="modal-head">
                <h3 style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text)', margin: 0 }}>Add New Page</h3>
                <button
                  onClick={() => setShowAddPage(false)}
                  className="btn btn-ghost btn-icon btn-sm"
                >
                  <X style={{ width: 16, height: 16 }} />
                </button>
              </div>

              <div className="modal-body col gap-4">
                <div>
                  <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 500, color: 'var(--text-soft)', marginBottom: 6 }}>
                    Page name
                  </label>
                  <input
                    type="text"
                    value={newPageName}
                    onChange={handlePageNameChange}
                    placeholder="e.g. Menu, About, Contact"
                    autoFocus
                    className="input w-full"
                    onKeyDown={(e) => e.key === 'Enter' && handleAddPage()}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 500, color: 'var(--text-soft)', marginBottom: 6 }}>
                    URL slug
                  </label>
                  <input
                    type="text"
                    value={newPageSlug}
                    onChange={(e) => setNewPageSlug(e.target.value)}
                    placeholder="/menu"
                    className="input w-full"
                    style={{ fontFamily: 'var(--font-mono)' }}
                  />
                  <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 4, marginBottom: 0 }}>
                    Links to this slug in your HTML will switch to this page in the preview
                  </p>
                </div>
              </div>

              <div className="modal-foot gap-2">
                <button
                  className="btn btn-ghost grow"
                  onClick={() => setShowAddPage(false)}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary grow"
                  onClick={handleAddPage}
                  disabled={!newPageName.trim() || addPageMutation.isLoading}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
                >
                  {addPageMutation.isLoading
                    ? <Loader2 style={{ width: 14, height: 14 }} className="animate-spin" />
                    : <Plus style={{ width: 14, height: 14 }} />}
                  Create page
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Delete confirmation modal ── */}
      <AnimatePresence>
        {deletingPageId && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="modal-backdrop"
            onClick={() => setDeletingPageId(null)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.15 }}
              onClick={(e) => e.stopPropagation()}
              className="modal"
              style={{ maxWidth: 400 }}
            >
              <div className="modal-head">
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ padding: 8, background: 'color-mix(in srgb, var(--danger) 12%, transparent)', borderRadius: 'var(--radius)' }}>
                    <Trash2 style={{ width: 20, height: 20, color: 'var(--danger)' }} />
                  </div>
                  <h3 style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text)', margin: 0 }}>Delete page?</h3>
                </div>
              </div>

              <div className="modal-body">
                <p style={{ fontSize: '0.875rem', color: 'var(--text-soft)', margin: 0 }}>
                  "{pages.find(p => p.id === deletingPageId)?.name}" will be permanently deleted. This cannot be undone.
                </p>
              </div>

              <div className="modal-foot gap-2">
                <button className="btn btn-ghost grow" onClick={() => setDeletingPageId(null)}>
                  Cancel
                </button>
                <button
                  className="btn btn-danger grow"
                  onClick={() => deletePageMutation.mutate(deletingPageId)}
                  disabled={deletePageMutation.isLoading}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
                >
                  {deletePageMutation.isLoading
                    ? <Loader2 style={{ width: 14, height: 14 }} className="animate-spin" />
                    : <Trash2 style={{ width: 14, height: 14 }} />}
                  Delete
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Media picker modal ── */}
      <AnimatePresence>
        {showMediaPicker && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="modal-backdrop"
            onClick={() => setShowMediaPicker(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 10 }}
              transition={{ duration: 0.15 }}
              onClick={(e) => e.stopPropagation()}
              className="modal"
              style={{ maxWidth: 672, maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}
            >
              <div className="modal-head" style={{ flexShrink: 0 }}>
                <div>
                  <h3 style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text)', margin: 0 }}>Media Library</h3>
                  <p style={{ fontSize: '0.75rem', color: 'var(--text-soft)', marginTop: 2, marginBottom: 0 }}>
                    Click an image to insert it into your {activeTab.toUpperCase()}
                  </p>
                </div>
                <button onClick={() => setShowMediaPicker(false)} className="btn btn-ghost btn-icon btn-sm">
                  <X style={{ width: 16, height: 16 }} />
                </button>
              </div>

              <div className="modal-body" style={{ flex: 1, overflowY: 'auto' }}>
                {mediaItems.length === 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '64px 0', color: 'var(--text-muted)' }}>
                    <Image style={{ width: 40, height: 40, marginBottom: 12, opacity: 0.3 }} />
                    <p style={{ fontSize: '0.875rem', margin: 0 }}>No media uploaded for this client yet</p>
                  </div>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
                    {mediaItems.map((item) => {
                      const url = item.public_url || item.file_path || ''
                      return (
                        <button
                          key={item.id}
                          onClick={() => insertMediaAtCursor(url)}
                          style={{
                            position: 'relative',
                            borderRadius: 'var(--radius)',
                            overflow: 'hidden',
                            border: '1px solid var(--border)',
                            aspectRatio: '1',
                            background: 'var(--bg-1)',
                            cursor: 'pointer',
                            padding: 0,
                            transition: 'border-color 0.15s',
                          }}
                          onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--accent)'}
                          onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}
                          className="group"
                        >
                          <img
                            src={item.thumbnail_url || url}
                            alt={item.original_name}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                          <div
                            style={{
                              position: 'absolute',
                              inset: 0,
                              background: 'rgba(0,0,0,0)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              transition: 'background 0.15s',
                            }}
                            onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,0,0,0.4)'}
                            onMouseLeave={e => e.currentTarget.style.background = 'rgba(0,0,0,0)'}
                          >
                            <span style={{
                              color: '#fff',
                              fontSize: '0.75rem',
                              fontWeight: 500,
                              padding: '4px 8px',
                              background: 'var(--accent)',
                              borderRadius: 'var(--radius)',
                              opacity: 0,
                              transition: 'opacity 0.15s',
                            }}
                              className="group-hover:opacity-100"
                            >
                              Insert
                            </span>
                          </div>
                          <div style={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            padding: 8,
                            background: 'linear-gradient(to top, rgba(0,0,0,0.6), transparent)',
                            opacity: 0,
                            transition: 'opacity 0.15s',
                          }}
                            className="group-hover:opacity-100"
                          >
                            <p style={{ color: '#fff', fontSize: '0.75rem', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.original_name}</p>
                          </div>
                        </button>
                      )
                    })}
                  </div>
                )}
              </div>

              <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--border)', flexShrink: 0, padding: '16px 24px' }}>
                <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', margin: 0 }}>
                  In HTML: inserts <code style={{ background: 'var(--bg-1)', padding: '1px 4px', borderRadius: 'var(--radius-sm)' }}>&lt;img src="..."&gt;</code> · In CSS: inserts <code style={{ background: 'var(--bg-1)', padding: '1px 4px', borderRadius: 'var(--radius-sm)' }}>url('...')</code> · In JS: inserts the URL string
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}


export default CodeEditor
